import { redirect } from '@tanstack/react-router'
import type { AppAbility, PermissionTuple } from '~/utils/ability'
import { resolveMissingPermissions, type PermissionRequirement } from '~/utils/rbac'
import type { GuardedTab } from '~/utils/rbacTabs'

/** Chaque onglet a sa route dédiée sous `/development/real-estate/`. */
export type RealEstateTab =
  | 'map'
  | 'kanban'
  | 'tasks'
  | 'calendar'
  | 'contacts'
  | 'interest-zones'
  | 'cartes-validees'

export const MAP_READ: PermissionTuple = ['read', 'real-estate.map.map']
export const MAP_ADD: PermissionTuple = ['add', 'real-estate.map.map']
export const MAP_EDIT: PermissionTuple = ['edit', 'real-estate.map.map']
export const MAP_DELETE: PermissionTuple = ['delete', 'real-estate.map.map']
export const MAP_EXPORT: PermissionTuple = ['export', 'real-estate.map.map']
/** Import du pivot des ouvertures (villes de pré-projet), sur la carte uniquement. */
export const MAP_IMPORT: PermissionTuple = ['import', 'real-estate.map.map']

export const KANBAN_READ: PermissionTuple = ['read', 'real-estate.kanban.project']
export const KANBAN_ADD: PermissionTuple = ['add', 'real-estate.kanban.project']
export const KANBAN_EDIT: PermissionTuple = ['edit', 'real-estate.kanban.project']
export const KANBAN_DELETE: PermissionTuple = ['delete', 'real-estate.kanban.project']
export const KANBAN_EXPORT: PermissionTuple = ['export', 'real-estate.kanban.project']

/**
 * Un projet se crée, s'édite et se supprime indifféremment depuis la carte ou depuis le kanban :
 * ses affordances restent actionnables avec le droit de l'un OU de l'autre.
 */
export const PROJECT_READ: PermissionRequirement = { any: [MAP_READ, KANBAN_READ] }
export const PROJECT_ADD: PermissionRequirement = { any: [MAP_ADD, KANBAN_ADD] }
export const PROJECT_EDIT: PermissionRequirement = { any: [MAP_EDIT, KANBAN_EDIT] }
export const PROJECT_DELETE: PermissionRequirement = { any: [MAP_DELETE, KANBAN_DELETE] }
export const PROJECT_EXPORT: PermissionRequirement = { any: [MAP_EXPORT, KANBAN_EXPORT] }

/**
 * Archiver / reprendre un projet : action spéciale, exigée avec l'action de base de sa ressource.
 * Même exigence côté backend, dans `updateStoreProject` (la ressource n'y est connue qu'au runtime,
 * l'archivage passant par le `PATCH /store-projects/:id` générique).
 */
export const PROJECT_ARCHIVE: PermissionRequirement = [
  ['edit', 'real-estate.kanban.archive'],
  ['archive-real-estate-project', 'real-estate.kanban.archive'],
]

/**
 * Documents et photos d'un projet — ressource propre depuis l'audit du 19/09 (TAX-M13) : ils
 * relevaient de l'`edit` du kanban ou de la carte, alors que l'onglet « Documents » porte des
 * pièces (baux, autorisations) qui n'ont rien à voir avec la fiche projet.
 */
export const DOCUMENT_READ: PermissionTuple = ['read', 'real-estate.kanban.document']
export const DOCUMENT_ADD: PermissionTuple = ['add', 'real-estate.kanban.document']
export const DOCUMENT_EDIT: PermissionTuple = ['edit', 'real-estate.kanban.document']
export const DOCUMENT_DELETE: PermissionTuple = ['delete', 'real-estate.kanban.document']

/**
 * Référentiel des pré-projets et des villes (points d'intérêt de la carte) — ressource propre
 * depuis l'audit du 19/09 (TAX-M13).
 */
export const PRE_PROJECT_READ: PermissionTuple = ['read', 'real-estate.map.pre-project']
export const PRE_PROJECT_EDIT: PermissionTuple = ['edit', 'real-estate.map.pre-project']
export const PRE_PROJECT_DELETE: PermissionTuple = ['delete', 'real-estate.map.pre-project']
export const PRE_PROJECT_IMPORT: PermissionTuple = ['import', 'real-estate.map.pre-project']

export const TASK_READ: PermissionTuple = ['read', 'real-estate.tasks.task']
export const TASK_ADD: PermissionTuple = ['add', 'real-estate.tasks.task']
export const TASK_EDIT: PermissionTuple = ['edit', 'real-estate.tasks.task']
export const TASK_DELETE: PermissionTuple = ['delete', 'real-estate.tasks.task']

export const EVENT_READ: PermissionTuple = ['read', 'real-estate.calendar.calendar']
export const EVENT_ADD: PermissionTuple = ['add', 'real-estate.calendar.calendar']
export const EVENT_EDIT: PermissionTuple = ['edit', 'real-estate.calendar.calendar']
export const EVENT_DELETE: PermissionTuple = ['delete', 'real-estate.calendar.calendar']

export const CONTACT_READ: PermissionTuple = ['read', 'real-estate.contacts.contact']
export const CONTACT_ADD: PermissionTuple = ['add', 'real-estate.contacts.contact']
export const CONTACT_EDIT: PermissionTuple = ['edit', 'real-estate.contacts.contact']
export const CONTACT_DELETE: PermissionTuple = ['delete', 'real-estate.contacts.contact']

/**
 * Onglet « Cartes validées » compris : la taxonomie n'expose pas de ressource propre aux cartes
 * sauvegardées, qui ne sont qu'un recueil de zones d'intérêt.
 */
export const AREA_READ: PermissionTuple = ['read', 'real-estate.areas-of-interest.area']
export const AREA_ADD: PermissionTuple = ['add', 'real-estate.areas-of-interest.area']
export const AREA_EDIT: PermissionTuple = ['edit', 'real-estate.areas-of-interest.area']
export const AREA_DELETE: PermissionTuple = ['delete', 'real-estate.areas-of-interest.area']

/**
 * Source unique des droits de lecture des sous-onglets. Consommée par la barre d'onglets, par le
 * `staticData.permission` de la route et par la redirection de l'index : un seul endroit à mettre à
 * jour, pas de dérive possible entre l'onglet affiché et son contenu.
 */
export const REAL_ESTATE_TAB_PERMISSIONS = {
  map: MAP_READ,
  kanban: KANBAN_READ,
  tasks: TASK_READ,
  calendar: EVENT_READ,
  contacts: CONTACT_READ,
  'interest-zones': AREA_READ,
  'cartes-validees': AREA_READ,
} as const satisfies Record<RealEstateTab, PermissionRequirement>

/** Accès à l'écran : dès qu'un seul de ses onglets est accessible. */
export const REAL_ESTATE_PAGE_READ: PermissionRequirement = {
  any: [MAP_READ, KANBAN_READ, TASK_READ, EVENT_READ, CONTACT_READ, AREA_READ],
}

/** Route dédiée de chaque onglet — cible de la barre d'onglets et des redirections. */
export const REAL_ESTATE_TAB_ROUTES = {
  map: '/development/real-estate/map',
  kanban: '/development/real-estate/kanban',
  tasks: '/development/real-estate/tasks',
  calendar: '/development/real-estate/calendar',
  contacts: '/development/real-estate/contacts',
  'interest-zones': '/development/real-estate/interest-zones',
  'cartes-validees': '/development/real-estate/cartes-validees',
} as const satisfies Record<RealEstateTab, string>

/**
 * `beforeLoad` des routes d'onglet : un onglet interdit renvoie vers l'index, qui redirige vers le
 * premier onglet autorisé (l'accès à l'écran est déjà refusé par le layout si aucun ne l'est).
 */
export const realEstateTabGuard =
  (tab: RealEstateTab) =>
  ({ context }: { context: { ability: AppAbility } }): void => {
    const missing = resolveMissingPermissions(context.ability, REAL_ESTATE_TAB_PERMISSIONS[tab])
    if (missing.length > 0) {
      throw redirect({ to: '/development/real-estate', replace: true })
    }
  }

type RealEstateTabDefinition = GuardedTab<RealEstateTab> & {
  label: string
  permission: PermissionRequirement
}

/** Ordre de déclaration = ordre d'affichage, et ordre de repli quand l'onglet actif est interdit. */
export const REAL_ESTATE_TABS: RealEstateTabDefinition[] = [
  { value: 'map', label: 'Carte', permission: REAL_ESTATE_TAB_PERMISSIONS.map },
  { value: 'kanban', label: 'Kanban', permission: REAL_ESTATE_TAB_PERMISSIONS.kanban },
  { value: 'tasks', label: 'Tâches', permission: REAL_ESTATE_TAB_PERMISSIONS.tasks },
  { value: 'calendar', label: 'Calendrier', permission: REAL_ESTATE_TAB_PERMISSIONS.calendar },
  { value: 'contacts', label: 'Contacts', permission: REAL_ESTATE_TAB_PERMISSIONS.contacts },
  {
    value: 'interest-zones',
    label: "Zones d'intérêt",
    permission: REAL_ESTATE_TAB_PERMISSIONS['interest-zones'],
  },
  {
    value: 'cartes-validees',
    label: 'Cartes validées',
    permission: REAL_ESTATE_TAB_PERMISSIONS['cartes-validees'],
  },
]
